/**
 * Google Calendar Account Management Service
 *
 * Handles multi-account CRUD operations, account status management, and
 * account-related API calls using functional patterns.
 */

import { googleApiClient } from './client'
import type {
  GoogleAccount,
  GoogleAccountStatus,
  GoogleAccountsResponse,
  GoogleAccountOperationResponse,
  AuthenticationRequest,
  AuthenticationResponse,
} from './types'

// ============================================================================
// Account Functions
// ============================================================================

/**
 * Get all Google accounts for a user
 * @param userId NotePlan user ID
 * @returns Array of Google accounts
 */
export async function getAccounts(userId: string): Promise<GoogleAccount[]> {
  const response = await googleApiClient.get<GoogleAccountsResponse>(
    `accounts.php?userid=${encodeURIComponent(userId)}`
  )
  return response.data.accounts
}

// ============================================================================
// Account Creation Functions
// ============================================================================

/**
 * Add a new Google account for a user
 * @param params Authentication request parameters
 * @returns Authentication response with new account info
 */
export async function addAccount(
  parameters: AuthenticationRequest
): Promise<AuthenticationResponse> {
  const response = await googleApiClient.post<AuthenticationResponse>(
    'accounts.php',
    parameters
  )
  return response.data
}

// ============================================================================
// Account Deletion Functions
// ============================================================================

/**
 * Remove a Google account for a user
 * @param userId NotePlan user ID
 * @param accountId Google account ID to remove
 * @returns Operation response
 */
export async function removeAccount(
  userId: string,
  accountId: string
): Promise<GoogleAccountOperationResponse> {
  const response = await googleApiClient.delete<GoogleAccountOperationResponse>(
    `accounts.php?userid=${encodeURIComponent(userId)}&account_id=${encodeURIComponent(accountId)}`
  )
  return response.data
}

// ============================================================================
// Account Status Management Functions
// ============================================================================

/**
 * Update the status of a Google account
 * @param userId NotePlan user ID
 * @param accountId Google account ID
 * @param status New account status
 * @returns Operation response
 */
export async function updateAccountStatus(
  userId: string,
  accountId: string,
  status: GoogleAccountStatus
): Promise<GoogleAccountOperationResponse> {
  const response = await googleApiClient.put<GoogleAccountOperationResponse>(
    'accounts.php',
    {
      userid: userId,
      account_id: accountId,
      status,
    }
  )
  return response.data
}
